import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/ui/Icon';
import { ConfirmationModal } from '@/components/ui/ConfirmationModal';
import { CalendarManagementModal } from '@/components/calendar/CalendarManagementModal';
import { useCalendars, useDeleteCalendar } from '@/hooks/useCalendar';
import { useTimezone } from '@/contexts/TimezoneContext';
import { Calendar } from '@/types/calendar';
import { ICONS } from '@/constants/icons';
import { devLog } from '@/utils/logger';

/**
 * CalendarSettings Component
 *
 * Modern settings page for managing user calendars
 * Displays list of calendars with dropdown actions
 */

export const CalendarSettings = () => {
  const { t } = useTranslation('settings');
  const { formatDateOnly } = useTimezone();
  const { data: calendars, isLoading } = useCalendars({ isActive: true });
  const deleteMutation = useDeleteCalendar();

  const [editingCalendar, setEditingCalendar] = useState<Calendar | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    isOpen: boolean;
    calendar: Calendar | null;
  }>({ isOpen: false, calendar: null });

  const handleDelete = async () => {
    if (!deleteConfirm.calendar) return;

    try {
      await deleteMutation.mutateAsync(deleteConfirm.calendar.id);
      setDeleteConfirm({ isOpen: false, calendar: null });
    } catch (error) {
      devLog.error('Failed to delete calendar:', error);
    }
  };

  const handleCloseModal = () => {
    setShowCreateModal(false);
    setEditingCalendar(null);
  };

  if (isLoading) {
    return (
      <div className="space-y-6 sm:space-y-8">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">{t('settings_calendar_header_title')}</h2>
          <p className="mt-2 text-gray-600">
            {t('settings_calendar_header_description')}
          </p>
        </div>
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="py-16 text-center">
            <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-primary-600" />
            <p className="mt-4 font-medium text-gray-600">{t('settings_calendar_loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">{t('settings_calendar_header_title')}</h2>
        <p className="mt-2 text-gray-600">
          {t('settings_calendar_header_description')}
        </p>
      </div>

      {/* Calendar List Card */}
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-100 bg-gray-50/50 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">{t('settings_calendar_section_title')}</h3>
            {calendars && calendars.length > 0 && (
              <span className="rounded-full bg-primary-100 px-2.5 py-0.5 text-sm font-semibold text-primary-700">
                {calendars.length}
              </span>
            )}
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-xl bg-gray-900 text-white text-xs sm:text-sm font-semibold hover:shadow-md transition-all duration-200"
          >
            <Icon name={ICONS.PLUS} size="sm" color="text-white" ariaHidden />
            {t('settings_calendar_create_button')}
          </button>
        </div>

        {calendars && calendars.length > 0 ? (
          <div className="divide-y divide-gray-100">
            {calendars.map((calendar) => (
              <div
                key={calendar.id}
                className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 hover:bg-gray-50/50 transition-colors"
              >
                <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                  {/* Color circle - larger and rounded */}
                  <div
                    className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl flex-shrink-0 shadow-sm"
                    style={{ backgroundColor: calendar.color }}
                    aria-hidden="true"
                  />

                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm sm:text-base font-semibold text-gray-900">{calendar.name}</p>
                      {calendar.isDefault && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                          <Icon name={ICONS.CHECK} size="xs" ariaHidden />
                          {t('settings_calendar_badge_default')}
                        </span>
                      )}
                    </div>
                    <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
                      {t('settings_calendar_created_date', { date: formatDateOnly(calendar.createdAt) })}
                    </p>
                  </div>
                </div>

                {/* Actions Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setOpenDropdown(openDropdown === calendar.id ? null : calendar.id)}
                    className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                    aria-label={t('calendar:calendar_actions', { defaultValue: 'Calendar actions' })}
                  >
                    <Icon name={ICONS.ELLIPSIS_VERTICAL} size="sm" ariaHidden />
                  </button>

                  {openDropdown === calendar.id && (
                    <>
                      {/* Backdrop to close dropdown */}
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setOpenDropdown(null)}
                      />

                      {/* Dropdown Menu */}
                      <div className="absolute right-0 top-full mt-1 z-20 w-48 rounded-xl bg-white border border-gray-200 shadow-lg overflow-hidden">
                        <button
                          onClick={() => {
                            setEditingCalendar(calendar);
                            setOpenDropdown(null);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          <Icon name={ICONS.EDIT} size="sm" color="text-gray-400" ariaHidden />
                          {t('settings_calendar_edit_action')}
                        </button>
                        {!calendar.isDefault && (
                          <button
                            onClick={() => {
                              setDeleteConfirm({ isOpen: true, calendar });
                              setOpenDropdown(null);
                            }}
                            className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors"
                          >
                            <Icon name={ICONS.TRASH} size="sm" color="text-red-400" ariaHidden />
                            {t('settings_calendar_delete_action')}
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="py-16 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
              <Icon name={ICONS.CALENDAR} size="lg" color="text-gray-400" ariaHidden />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">{t('settings_calendar_empty_title')}</h3>
            <p className="mx-auto mt-2 max-w-sm text-gray-600">
              {t('settings_calendar_empty_description')}
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-6 inline-flex items-center gap-2 w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base rounded-lg sm:rounded-xl bg-gray-900 text-white font-semibold hover:shadow-md transition-all duration-200"
            >
              <Icon name={ICONS.PLUS} size="sm" color="text-white" ariaHidden />
              {t('settings_calendar_create_button')}
            </button>
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="flex items-start gap-3 rounded-xl border border-light-teal-gray bg-soft-mint p-4">
        <Icon name={ICONS.INFO} size="sm" color="text-dark-teal" ariaHidden />
        <div className="text-sm text-dark-teal">
          <strong>{t('settings_calendar_info_label')}</strong> {t('settings_calendar_info_content')}
        </div>
      </div>

      {/* Create/Edit Modal */}
      <CalendarManagementModal
        isOpen={showCreateModal || !!editingCalendar}
        onClose={handleCloseModal}
        editCalendar={editingCalendar}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        open={deleteConfirm.isOpen}
        onOpenChange={(open) => !open && setDeleteConfirm({ isOpen: false, calendar: null })}
        title={t('settings_modal_delete_calendar_title')}
        description={
          deleteConfirm.calendar
            ? t('settings_modal_delete_calendar_description', { name: deleteConfirm.calendar.name })
            : ''
        }
        variant="danger"
        confirmText={t('settings_modal_delete_confirm')}
        cancelText={t('settings_modal_cancel')}
        onConfirm={handleDelete}
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
};
